import styles from './customer-product-list.module.scss';
import { Col, Row } from 'reactstrap';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import { useState } from 'react';
import { useGetRequestNewHook, useTableConfigHook } from '../../requestUtils';
import PageTitle from '../../page-title/page-title';
import { ValueFormatterParams } from 'ag-grid-community';
import { UseSignalRHook } from '../../signalr-context/signalr-context';
import { Link } from 'react-router-dom';
import CustomerProductListGridRenderer from '../customer-product-list-grid-renderer/customer-product-list-grid-renderer';
import { booleanHeader, Box, Container, CreateHeaders, currencyHeader, dateHeader, FutureChangeIcon, HeaderSizeEnum, IContactType, IWebkuaSignalRResponse, OrderTypesEnum, Tab, Tabs, textHeader, Tooltip } from '@frontend-workspace/react-shared-lib';
import CustomerProductListSkeleton from './customer-product-list-skeleton';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import TrialLicenses from '../../trial-licenses/trial-licenses';
import { ICustomerProducts } from '../../_models/customer-products';
import { SalesforceRecordLink } from '../../salesforce-navigation/salesforce-navigation';

/* eslint-disable-next-line */
export interface CustomerProductListProps {
  currentCompany: IContactType,
  salesforceBaseUrl?: string
}

export function CustomerProductList({ salesforceBaseUrl }: { salesforceBaseUrl?: string }) {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={t('app.productList')} showNoCompanyMessage={!currentCompany} />
            {currentCompany ? <CustomerProductListContent
              salesforceBaseUrl={salesforceBaseUrl}
              currentCompany={currentCompany}
            /> : <CustomerProductListSkeleton />}
          </Box>
        </Col>
      </Row>
    </Container>
  )
}

function CustomerProductListContent(props: CustomerProductListProps) {
  const { t } = useTranslation();
  const companyNumber = props.currentCompany ? props.currentCompany.number : "";
  const tableConfig = useTableConfigHook("CustomerProductList");
  const [forGroup, setForGroup] = useState<boolean>(false);
  const [selectedTab, setSelctedTab] = useState<number>(0);
  const [onlyActive, setOnlyActive] = useState<boolean>(true);
  const { data: licenses, getData, isLoading } = useGetRequestNewHook(
    `companies/${companyNumber}/licenses/forGroup/${forGroup}/onlyActive/${onlyActive}`,
    [], [props.currentCompany, forGroup, onlyActive]);


  const handleAccessChange = (response: IWebkuaSignalRResponse) => getData();
  UseSignalRHook(handleAccessChange, [OrderTypesEnum.ChangeLicenseAccess, OrderTypesEnum.ChangeDomain, OrderTypesEnum.LicenseAccessChangeOrder]);

  const moduleRenderer = (params: ValueFormatterParams) => {
    if (params.value.length > 0) {
      return <Tooltip title={params.value.map((m: string) => <div>{m}</div>)}>{params.value.length}</Tooltip>
    } else {
      return (
        params.value.length
      );
    }
  };

  const contractNumberRenderer = (params: {
    value?: string; data?: Pick<ICustomerProducts, 'companyNumber' | 'isSummary'>
  }) => {
    if (!params.value || params.data?.isSummary) return params.value ?? null;
    const owner = params.data?.companyNumber ?? companyNumber;
    return <span className="d-inline-flex align-items-center gap-2">
      <Link to={`/customers/${encodeURIComponent(owner)}/contracts/${encodeURIComponent(params.value)}`}>{params.value}</Link>
      {props.salesforceBaseUrl && <SalesforceRecordLink baseUrl={props.salesforceBaseUrl}
        recordType="Contract" destination="contractAssets"
        companyNumber={params.data?.companyNumber ?? ''} contractNumber={params.value} />}
    </span>;
  };

  const futureOrderRenderer = (params: ValueFormatterParams) => {
    const futureOrders = params.value;
    if (!futureOrders) return null;
    return (
      futureOrders.map((order: React.ComponentProps<typeof FutureChangeIcon>['item']) =>
        <FutureChangeIcon key={order.id} item={order} />)
    );
  };

  const headers = CreateHeaders([
    textHeader("licenseNumber", t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM, {
      renderer: 'agGroupCellRenderer',
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    textHeader("companyName", t('standard.companyName'), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("place", t('standard.place'), HeaderSizeEnum.LARGE, { hide: !forGroup }),
    textHeader("companyNumber", t('standard.companyNo'), HeaderSizeEnum.MEDIUM, { hide: !forGroup }),
    textHeader("productName", t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("typeName", t('standard.type'), HeaderSizeEnum.MEDIUM),
    textHeader("numberOfSeats", t('standard.seats'), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("futureOrders", t('standard.changes'), HeaderSizeEnum.MEDIUM, { renderer: futureOrderRenderer }),
    textHeader("approvedDomains", t('customerProductList.domains'), HeaderSizeEnum.LARGE),
    textHeader("numberOfUsers", t('standard.accesses'), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("numberOfCoordinators", t('customerProductList.coordinators'), HeaderSizeEnum.LARGE, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("contractNumber", t('standard.contract'), HeaderSizeEnum.LARGE, {
      renderer: contractNumberRenderer, minWidth: 220
    }),
    textHeader("moduleNames", t('standard.modules'), HeaderSizeEnum.MEDIUM, {
      renderer: moduleRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("nextInvoiceCost", t('standard.invoiceCost'), HeaderSizeEnum.LARGE, { hide: true }),
    booleanHeader("hasLicensing", t('customerProductList.hasLicensing'), { hide: true }),
    booleanHeader("isOffline", t('standard.offline')),
    booleanHeader("isActive", t('standard.active'), { hide: onlyActive }),
    dateHeader("nextInvoiceDate", t('standard.nextInvoice'), { hide: true }),
    textHeader("itemInfoIds", t('customerProductList.items'), HeaderSizeEnum.MEDIUM, { hide: true })
  ]);

  return (
    <>
      <Tabs
        value={selectedTab}
        variant="scrollable"
        scrollButtons="auto">
        <Tab label={t("standard.licenses")} onClick={() => setSelctedTab(0)} />
        <Tab label={t("standard.trialLicenses")} onClick={() => setSelctedTab(1)} />
      </Tabs>
      {selectedTab === 0 && <WebkuaTable
        isLoading={isLoading}
        headers={headers}
        expandComponent={CustomerProductListGridRenderer}
        data={licenses}
        pageSize={15}
        tableConfig={tableConfig}
        tableFilters={[{
          label: t('customerProductList.forGroup'),
          checked: forGroup,
          onChange: setForGroup
        }, {
          label: t('standard.activeOnly'),
          checked: onlyActive,
          onChange: setOnlyActive
        }]}
      />}
      {selectedTab === 1 && <TrialLicenses />}
    </>
  );
}

export default CustomerProductList;